import React, {useState, useRef, useImperativeHandle} from 'react'
import Menu
    from '@mui/material/Menu'
import MenuItem
    from '@mui/material/MenuItem'
import {colors, radius} from "../RecipeDesigner/styles/designTokens";


/**
 * Use as a drop-in replacement for `<MenuItem>` when you need to add cascading
 * menu elements as children to this component.
 */
const NestedProductMarketingMenuContainer = React.forwardRef(function NestedProductMarketingMenuContainer(props, ref) {
  const {
    parentMenuOpen,
    component = 'div',
    to,
    label,
    children,
    className,
    tabIndex: tabIndexProp,
    MenuProps = {},
    ContainerProps: ContainerPropsProp = {},
    ...MenuItemProps
  } = props

  const {ref: containerRefProp, ...ContainerProps} = ContainerPropsProp

  const menuItemRef = useRef(null)
  useImperativeHandle(ref, () => menuItemRef.current)

  const containerRef = useRef(null)
  useImperativeHandle(containerRefProp, () => containerRef.current)

  const menuContainerRef = useRef(null)

  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false)

  const handleMouseEnter = (event) => {
    setIsSubMenuOpen(true)

    if (ContainerProps?.onMouseEnter) {
      ContainerProps.onMouseEnter(event)
    }
  }
  const handleMouseLeave = (event) => {
    setIsSubMenuOpen(false)

    if (ContainerProps?.onMouseLeave) {
      ContainerProps.onMouseLeave(event)
    }
  }

  // Check if any immediate children are active
  const isSubmenuFocused = () => {
    const active = containerRef.current?.ownerDocument?.activeElement
    for (const child of menuContainerRef.current?.children ?? []) {
      if (child === active) {
        return true
      }
    }
    return false
  }

  const handleFocus = (event) => {
    if (event.target === containerRef.current) {
      setIsSubMenuOpen(true)
    }

    if (ContainerProps?.onFocus) {
      ContainerProps.onFocus(event)
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      return
    }

    if (isSubmenuFocused()) {
      event.stopPropagation()
    }

    const active = containerRef.current?.ownerDocument?.activeElement

    if (event.key === 'ArrowLeft' && isSubmenuFocused()) {
      containerRef.current?.focus()
    }

    if (
      event.key === 'ArrowRight' &&
      event.target === containerRef.current &&
      event.target === active
    ) {
      const firstChild = menuContainerRef.current?.children[0]
      firstChild?.focus()
    }
  }

  const open = isSubMenuOpen && parentMenuOpen


  // Root element must have a `tabIndex` attribute for keyboard navigation
  let tabIndex
  if (!props.disabled) {
    tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1
  }

  return (
    <div
      {...ContainerProps}
      ref={containerRef}
      onFocus={handleFocus}
      tabIndex={tabIndex}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
    >
      <MenuItem
        {...MenuItemProps}
        ref={menuItemRef}
        component={component}
        to={to}
        aria-haspopup="true"
        aria-expanded={open}
        sx={{
          alignItems: 'flex-start',
          whiteSpace: 'normal',
          minHeight: 'auto',
          mx: '6px',
          my: '1px',
          px: '10px',
          py: '9px',
          borderRadius: radius.md,
          textDecoration: 'none',
          transition: 'background-color 120ms ease, color 120ms ease',
          backgroundColor: open ? colors.accent.primaryLight : 'transparent',
          '& svg': {
            color: open ? colors.accent.primary : colors.text.tertiary,
            transition: 'color 120ms ease',
          },
          '&:hover': {
            backgroundColor: open ? colors.accent.primaryLight : colors.accent.primarySubtle,
            '& svg': {color: colors.accent.primary},
          },
          '&.Mui-focusVisible': {
            backgroundColor: colors.accent.primaryLight,
            outline: `1px solid ${colors.accent.primaryBorderStrong}`,
            outlineOffset: '-1px',
          },
        }}
      >
        {label}
      </MenuItem>
      {children && children.length > 0 &&
      <Menu
        // Set pointer events to 'none' to prevent the invisible Popover div
        // from capturing events for clicks and hovers
        style={{pointerEvents: 'none'}}
        anchorEl={menuItemRef.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        open={open}
        autoFocus={false}
        disableAutoFocus
        disableEnforceFocus
        disableScrollLock
        transitionDuration={{enter: 120, exit: 80}}
        onClose={() => {
          setIsSubMenuOpen(false)
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              width: 340,
              ml: '-1px',
              mt: '-4px',
              borderRadius: radius.lg,
              border: `1px solid ${colors.border.default}`,
              boxShadow: '0 4px 16px rgba(15, 18, 30, 0.06)',
              overflow: 'hidden',
            },
          },
        }}
        MenuListProps={{dense: true, sx: {py: '6px'}}}
      >
        <div ref={menuContainerRef} style={{pointerEvents: 'auto'}}>
          {children}
        </div>
      </Menu>
          }
    </div>
  )
})

export default NestedProductMarketingMenuContainer;